import {FormattedMessage} from 'react-intl';
import {Grid, Card, CardContent, Typography, Theme, Tooltip, type CardProps, CardActionArea, CardActions, type ButtonBaseProps, type GridProps} from '@mui/material';
import {makeStyles} from '@mui/styles';
import type {BookmarkFolder} from 'types';
import {SVG_VIEWBOX_OFFSET} from 'constants/offsets';
import {useMobileScreen} from 'hooks';
import FolderIcon from 'components/icons/Folder';
import {AVATAR_SIZE} from '../AdminBookmarkCard';
import EditBookmarkFolderIconButton from './EditBookmarkFolderIconButton';
import DeleteBookmarkFolderIconButton from './DeleteBookmarkFolderIconButton';

const UP_LG_CARD_HEIGHT = 117;
const DOWN_MD_CARD_HEIGHT = 128;

const useStyles = makeStyles((theme : Theme) => ({
  card: {
    textAlign: 'left',
    position: 'relative',
    [theme.breakpoints.up('lg')]: {
      maxHeight: UP_LG_CARD_HEIGHT,
    },
    [theme.breakpoints.down('md')]: {
      minHeight: DOWN_MD_CARD_HEIGHT,
    }
  },
  cardContent: {
    [theme.breakpoints.up('lg')]: {
      maxHeight: UP_LG_CARD_HEIGHT,
    }
  },
  avatar: {
    width: AVATAR_SIZE + SVG_VIEWBOX_OFFSET,
    height: AVATAR_SIZE + SVG_VIEWBOX_OFFSET,
    marginLeft: - SVG_VIEWBOX_OFFSET / 2,
    marginTop: - SVG_VIEWBOX_OFFSET / 2,
    '& > svg': {
      filter: `drop-shadow(0px 0.5px 1px rgba(0,0,0,0.5))`,
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
    },
    [theme.breakpoints.down('md')]: {
      marginTop: '-3px',
    }
  },
  name: {
    fontWeight: '500',
  },
  actions: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    padding: theme.spacing(1.5, 2),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
    },
  }
}), {name: 'AdminBookmarkFolderCard'});

type AdminBookmarkFolderCardProps = CardProps & ButtonBaseProps & {
  folder: BookmarkFolder;
}

const AdminBookmarkFolderCard = ({folder, onClick} : AdminBookmarkFolderCardProps) => {
  const {key, name, bookmarks} = folder;
  const classes = useStyles();
  const isMobileScreenWidth = useMobileScreen();
  const actionsGridProps : Partial<GridProps> = isMobileScreenWidth
  ? {
      spacing: 2,
    }
  : {
      justifyContent: 'flex-end',
      spacing: 1,
    }
  return (
    <Card className={classes.card}>
      <CardActionArea className={classes.cardContent} onClick={onClick}>
        <CardContent className={classes.cardContent}>
          <Grid container wrap='nowrap' spacing={2}>
            <Grid item>
              <FolderIcon className={classes.avatar} color='secondary' />
            </Grid>
            <Grid item xxs zeroMinWidth>
              <Grid container spacing={1}>
                <Grid item zeroMinWidth xs={12}>
                  <Tooltip title={name} placement='right'>
                    <Typography className={classes.name} variant='h5' noWrap>{name}</Typography>
                  </Tooltip>
                </Grid>
                <Grid item zeroMinWidth xs={12}>
                  <Typography variant='body2' noWrap>
                    <FormattedMessage id="admin.bookmarks.folder.subtitle" values={{ numberOfBookmarks: bookmarks.length }} />
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </CardActionArea>
      <CardActions className={classes.actions}>
        <Grid container {...actionsGridProps}>
          <Grid item>
            <EditBookmarkFolderIconButton folderName={name} folderKey={key} />
          </Grid>
          <Grid item>
            <DeleteBookmarkFolderIconButton folderName={name} folderKey={key} />
          </Grid>
        </Grid>
      </CardActions>
    </Card>
  )
}

export default AdminBookmarkFolderCard;